import { useEffect, useRef } from "react";

import { MIN_ROW_HEIGHT } from "@corca/tiptap";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { InputSize } from "@/components/input/InputSize";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { useGetPMNodeDomRect } from "@/components/tiptap/utils/useGetPMNodeRect";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeWithPos } from "@tiptap/core";

type Props = {
  doc: DocumentModel;
  tableCell: NodeWithPos;
  value?: number | null;
  onChange: (value: number | null) => void;
};

export const TableRowHeight = observer(function TableRowHeight({
  doc,
  tableCell,
  value,
  onChange,
}: Props) {
  const { tiptap } = doc.editor;

  const t = useTranslations("sidebar.tableCellStyle");
  const { height } = useGetPMNodeDomRect(tiptap?.view, tableCell.pos) ?? {};
  const rowHeight = height ? Math.round(height) : undefined;
  const heightRef = useRef(height);

  const items = [
    {
      title: t("customHeight"),
      icon: <Icon name="CustomHeight" />,
      active: !!value,
      onClick: () => {
        onChange(rowHeight ?? MIN_ROW_HEIGHT);
      },
    },
    {
      title: t("autoWidth"),
      icon: <Icon name="AutoHeight" />,
      active: value === null,
      onClick: () => {
        onChange(null);
      },
    },
  ];

  useEffect(() => {
    if (!rowHeight) {
      return;
    }

    if (heightRef.current && heightRef.current < rowHeight) {
      onChange(null);
    }

    heightRef.current = value ?? undefined;
  }, [value, rowHeight, onChange]);

  return (
    <div className="relative">
      <InputSize
        value={value ?? ""}
        type="height"
        label={t("rowHeight")}
        min={MIN_ROW_HEIGHT}
        max={500}
        incrementFrom={height ? rowHeight : undefined}
        placeholder={height ? `${rowHeight}` : undefined}
        inputClassName="pr-3"
        onChange={onChange}
      />
      <Dropdown
        anchor={{ to: "bottom", padding: "10px" }}
        className="absolute top-0 right-0 bottom-0 w-3"
        toggleButtonLabel={t("heightSettings")}
        toggleButtonComponent={<Icon name="ArrowDown" className="[&>path]:stroke-document-text" />}
        items={items}
      />
    </div>
  );
});
